import Decimal from 'decimal.js';
import ApplicationError from '@/core/misc/ApplicationError';
import { ErrorSeverity } from '@/core/misc/ErrorSeverity';
import NumberUtils from '@/core/utils/NumberUtils';
import Core from "@/core/Core";

/**
 * Базовый тип-значение для точных вычислений.
 *
 * Принципы:
 * - внутри всегда Decimal, никогда native number (number = IEEE-754, теряет точность);
 * - экземпляр ВСЕГДА нормализован: ровно SCALE знаков после запятой.
 *   Округление вшито в конструктор, результат любой операции рождается
 *   уже округлённым до своего типа;
 * - сериализация — СТРОКА с фиксированным числом знаков ("1234.560"),
 *   строка переносит и значение, и scale без потерь на обоих концах (JS/PHP);
 * - неявное приведение к number запрещено (valueOf кидает).
 *
 * Человекочитаемое представление: format — забота наследников (валюта/юнит
 * зависят от типа: Sum → валюта, Qty → единица, Price → и то и другое).
 * Числовое ядро форматирования — NumberUtils.formatDecimal, одно на весь фронт.
 *
 * Зеркальный бекенд: core/types/Numeric.php на BcMath\Number.
 */
abstract class Numeric {
    /**
     * Режим округления ПО УМОЛЧАНИЮ.
     * half-up = away from zero (РФ-бухгалтерия): 2.5 → 3, -2.5 → -3.
     * Зеркало на беке: RoundingMode::HalfAwayFromZero.
     * Наследник вправе переопределить (Volume — безусловно вверх, ROUND_UP).
     */
    protected static readonly ROUNDING: Decimal.Rounding = Decimal.ROUND_HALF_UP;

    /**
     * Целевая точность типа. Наследник ОБЯЗАН переопределить.
     * Зеркало бекового const SCALE. TS не умеет abstract static —
     * первый же toFixed(undefined) всплывёт в тестах сходимости с PHP.
     */
    static readonly SCALE: number;

    /**
     * Внутреннее значение. Всегда нормализовано до SCALE.
     * Публичное — нужно операциям других типов (Qty.mul(Price) достаёт value
     * цены). decimal.js иммутабельна, испортить снаружи нельзя. Считать деньги
     * через нативные + и * запрещено (valueOf кидает).
     */
    readonly value: Decimal;

    /** Scale текущего типа — SCALE наследника через позднее связывание. */
    protected get scale(): number {
        return (this.constructor as typeof Numeric).SCALE;
    }

    /**
     * Конструктор САМ округляет значение до SCALE — инвариант нормализации.
     * SCALE и ROUNDING читаются через this.constructor (позднее связывание),
     * поэтому работают уже в super() и уважают переопределения наследника.
     * Вызов round именно через ctor, а НЕ через Numeric.round — иначе
     * this.ROUNDING внутри round связался бы с базовым классом и
     * переопределённый ROUNDING наследника молча игнорировался бы.
     */
    constructor(value: Decimal.Value) {
        const ctor = this.constructor as typeof Numeric;
        this.value = ctor.round(new Decimal(value), this.scale);
    }

    /**
     * ЕДИНСТВЕННАЯ функция округления на весь фронт.
     * Режим берётся из ROUNDING конкретного типа через позднее связывание this.
     * Меняем алгоритм здесь при отладке сходимости с PHP.
     */
    protected static round(value: Decimal, scale: number): Decimal {
        return value.toDecimalPlaces(scale, this.ROUNDING);
    }

    /**
     * Ноль в типе наследника: Sum.zero() → Sum, Qty.zero() → Qty.
     * Через позднее связывание this — не дублируется на каждом типе.
     */
    static zero<T extends Numeric>(this: new (value: Decimal.Value) => T): T {
        return new this(0);
    }

    /**
     * Пустое (не введённое) значение в типе наследника: Qty.fromEmpty() → Qty.
     * Внутреннее значение — NaN (isEmpty() === true). Создаётся ТОЛЬКО явно
     * через эту фабрику: пустое состояние в расчётах — частый источник ошибок,
     * поэтому оно должно быть намеренным, а не побочным эффектом вызова
     * конструктора без аргумента. Используется в fromNew/fromOrder для
     * инициализации полей ещё не заполненной сущности.
     *
     * Имя from* (не empty) — чтобы фабрика не читалась как предикат: пара
     * empty()/isEmpty() двусмысленна, fromEmpty()/isEmpty() — нет.
     */
    static fromEmpty<T extends Numeric>(this: new (value: Decimal.Value) => T): T {
        return new this(NaN);
    }

    /**
     * Значение из пользовательского ввода в типе наследника: Qty.fromInput("12,5") → Qty.
     * Разделитель — точка или запятая, разряды могут быть разделены пробелами
     * (разбор — NumberUtils.parseInput). Неразобранная строка даёт fromEmpty(),
     * а не исключение: ввод бывает любым.
     *
     * Конструктор при этом остаётся строгим (только точка): он для данных
     * сервера и результатов расчёта, а не для того, что набрал человек.
     */
    static fromInput<T extends Numeric>(this: new (value: Decimal.Value) => T, input: string | null): T {
        const parsed = NumberUtils.parseInput(input);

        if (parsed === null) {
            return new this(NaN);
        } else {
            return new this(parsed);
        }
    }

    /** Пустое значение отправляем как null, иначе на бэк уйдёт строка "NaN" */
    toJSON(): string | null {
        if (this.isEmpty() === true) {
            return null;
        }
        else {
            return this.value.toFixed(this.scale);
        }
    }

    /**
     * Человекочитаемое представление. База даёт дефолт — голое число с
     * разделителями разрядов и фиксированным scale. Наследники переопределяют
     * под себя: Weight/Dimension — без разделителей, Sum/Qty — с постфиксом
     * (валюта/юнит). Правило простое и одно на все типы: у значения есть format.
     * Пустое (не введённое) значение — пустая строка, не "NaN".
     */
    format(): string {
        if (this.isEmpty()) {
            return '';
        }

        return NumberUtils.formatDecimal(this.value, this.scale);
    }

    /** toString — то же, что format. Интерполяция ${x} даёт ровно то, что format():
     * одно представление, наследнику достаточно переопределить format. */
    toString(): string {
        return this.format();
    }

    equals(other: this | null | undefined): boolean {
        return this.value.equals(Numeric.toDecimal(other));
    }

    compare(other: this | null | undefined): -1 | 0 | 1 {
        return this.value.comparedTo(Numeric.toDecimal(other)) as -1 | 0 | 1;
    }

    /**
     * Приводит операнд к Decimal. Принимаем Numeric (берём его value),
     * примитив (число/строку/Decimal — заворачиваем в Decimal) и пустоту.
     * Позволяет greaterThan/lessThan сравнивать и с другим Numeric, и с
     * числовой границей (min/max в валидаторах), не дублируя методы, а
     * арифметике наследников — единообразно принимать операнд.
     *
     * null/undefined = NaN, то есть то же самое, что fromEmpty(): контролы
     * отдают null на очищенном поле, и операция с ним должна давать пустой
     * результат, а не падать на обращении к value. Дальше NaN расходится
     * по цепочке сам — decimal.js его распространяет, isEmpty() честно
     * отвечает true, format() печатает пустую строку.
     */
    protected static toDecimal(operand: Numeric | Decimal.Value | null | undefined): Decimal {
        if (operand === null || operand === undefined) {
            return new Decimal(NaN);
        } else if (operand instanceof Numeric) {
            return operand.value;
        } else {
            return new Decimal(operand);
        }
    }

    /** this строго больше operand (Numeric или число). */
    greaterThan(operand: this | Decimal.Value | null | undefined): boolean {
        return this.value.greaterThan(Numeric.toDecimal(operand));
    }

    /** this строго меньше operand (Numeric или число). */
    lessThan(operand: this | Decimal.Value | null | undefined): boolean {
        return this.value.lessThan(Numeric.toDecimal(operand));
    }

    /** this строго меньше operand (Numeric или число). */
    lessThanOrEqualTo(operand: this | Decimal.Value | null | undefined): boolean {
        return this.value.lessThanOrEqualTo(Numeric.toDecimal(operand));
    }

    /** this больше или равен operand (Numeric или число). */
    greaterThanOrEqualTo(operand: this | Decimal.Value | null | undefined): boolean {
        return this.value.greaterThanOrEqualTo(Numeric.toDecimal(operand));
    }

    isPositive(): boolean {
        return this.value.greaterThan(0);
    }

    isZero(): boolean {
        return this.value.isZero();
    }

    /**
     * Значение не задано: внутренний Decimal невалиден (NaN).
     * Numeric по построению нормализован, поэтому «пусто» выражается именно
     * невалидностью значения, а не отдельным null-полем. Используется для
     * отсечения «не введено» в UX-валидациях.
     */
    isEmpty(): boolean {
        return this.value.isNaN();
    }

    /**
     * Явное, осознанное приведение к native number.
     * Для случаев, где нужен именно примитив и потеря точности приемлема:
     * расчёт пропорций/ширины прогресс-баров, передача в сторонние UI-либы.
     * Бизнес-арифметику деньгами через native НЕ вести — для неё методы Numeric.
     */
    toNumber(): number {
        return this.value.toNumber();
    }

    /** Запрет неявного приведения к number (см. fixme от 27.01.2026 в старом Price). */
    valueOf(): never {
        const typeName = (this.constructor as any).__entityTypeName ?? 'Numeric';
        throw new ApplicationError(
            `${typeName}: неявное приведение к number запрещено.`,
            0,
            ErrorSeverity.Error,
            { typeName: typeName, value: this.value?.toString() },
        );
    }

    /**
     * "Умное" форматирование: без хвостовых нулей, с разделителем разрядов и
     * запятой в дробной части. В отличие от format(), который всегда печатает
     * ровно SCALE знаков.
     *
     * Реализация - в региональных утилитах, здесь только вызов: локаль не дело
     * расчётного типа. Так же устроены getVat и addVat у Sum и Price.
     *
     * Локаль пока жёстко русская - шаблоны, которые этим пользуются, локальные.
     * Когда появится вторая, выбор уедет в RegionalCommonUtils, а вызов останется.
     *
     * Аргументов у базовой реализации нет намеренно: приписка к числу у каждого
     * типа своя - у количества единица измерения, у суммы и цены валюта, - и
     * наследники добавляют её своим необязательным параметром. Появись он здесь,
     * переопределить его с другим типом стало бы нельзя.
     *
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     */
    public formatSmart(): string
    {
        if (this.isEmpty())
        {
            return '';
        }

        return Core.utils.regional.ru.formatDecimalSmart(this);
    }

    /**
     * Сокращённое форматирование: 12 500 → "12,5 тыс", 3 450 000 → "3,45 млн",
     * 12 345 000 000 → "12,35 млрд". Меньше тысячи — как formatSmart.
     * Для мест, где полное число не помещается: подписи прогресс-бара,
     * плитки, узкие колонки. Точное значение при этом теряется — рядом
     * стоит показывать полное (title, тултип).
     *
     * Реализация — NumberUtils.formatDecimalShorten. Аргументов у базовой
     * реализации нет по той же причине, что у formatSmart: приписку
     * (единицу, валюту) наследники добавляют своим параметром.
     *
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     */
    public formatShorten(): string
    {
        if (this.isEmpty())
        {
            return '';
        }

        return NumberUtils.formatDecimalShorten(this.value, this.scale);
    }
}

export default Numeric;